import { React, useEffect, useState } from "react";
import CustomInput from "../../components/CustomInput";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import {
  getSettings,
  updateSettings,
  uploadLogo,
  uploadFavicon,
  resetState,
} from "../../features/cms/settings/cmsSettingsSlice";

const Settings = () => {
  const dispatch = useDispatch();
  const settingsState = useSelector((state) => state.cmsSettings);
  // const imgState = useSelector((state) => state?.upload?.images);

  const { isSuccess, isError, isLoading, settings, updatedSettings } =
    settingsState;

  const [activeTab, setActiveTab] = useState("general");

  useEffect(() => {
    dispatch(resetState());
    dispatch(getSettings());
  }, [dispatch]);

  useEffect(() => {
    if (isSuccess && updatedSettings) {
      toast.success("Settings Updated Successfully!");
    }
    if (isError) {
      toast.error("Something Went Wrong!");
    }
  }, [isSuccess, isError, isLoading, updatedSettings]);

  const handleLogoUpload = (acceptedFiles) => {
    const formData = new FormData();
    formData.append("logo", acceptedFiles[0]);
    dispatch(uploadLogo(formData));
  };

  const handleFaviconUpload = (acceptedFiles) => {
    const formData = new FormData();
    formData.append("favicon", acceptedFiles[0]);
    dispatch(uploadFavicon(formData));
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      siteName: settings?.siteName || "",
      siteDescription: settings?.siteDescription || "",
      contactEmail: settings?.contactEmail || "",
      contactPhone: settings?.contactPhone || "",
      contactAddress: settings?.contactAddress || "",
      socialMedia: {
        facebook: settings?.socialMedia?.facebook || "",
        twitter: settings?.socialMedia?.twitter || "",
        instagram: settings?.socialMedia?.instagram || "",
        linkedin: settings?.socialMedia?.linkedin || "",
        youtube: settings?.socialMedia?.youtube || "",
      },
      paymentMethods: {
        stripe: {
          enabled: settings?.paymentMethods?.stripe?.enabled || false,
          publicKey: settings?.paymentMethods?.stripe?.publicKey || "",
        },
        razorpay: {
          enabled: settings?.paymentMethods?.razorpay?.enabled || false,
          keyId: settings?.paymentMethods?.razorpay?.keyId || "",
        },
        cod: {
          enabled: settings?.paymentMethods?.cod?.enabled || false,
        },
      },
      shipping: {
        freeShippingThreshold:
          settings?.shipping?.freeShippingThreshold || 0,
        defaultShippingCost: settings?.shipping?.defaultShippingCost || 0,
      },
      seo: {
        defaultMetaTitle: settings?.seo?.defaultMetaTitle || "",
        defaultMetaDescription: settings?.seo?.defaultMetaDescription || "",
        defaultMetaKeywords: settings?.seo?.defaultMetaKeywords || "",
        googleAnalyticsId: settings?.seo?.googleAnalyticsId || "",
        facebookPixelId: settings?.seo?.facebookPixelId || "",
      },
      maintenance: {
        isMaintenanceMode:
          settings?.maintenance?.isMaintenanceMode || false,
        maintenanceMessage:
          settings?.maintenance?.maintenanceMessage || "",
      },
      currency: {
        symbol: settings?.currency?.symbol || "$",
        code: settings?.currency?.code || "USD",
      },
    },
    onSubmit: (values) => {
      dispatch(updateSettings(values));
    },
  });

  const tabs = [
    { id: "general", label: "General" },
    { id: "contact", label: "Contact" },
    { id: "social", label: "Social Media" },
    { id: "payment", label: "Payment" },
    { id: "shipping", label: "Shipping" },
    { id: "seo", label: "SEO" },
    { id: "maintenance", label: "Maintenance" },
    { id: "currency", label: "Currency" },
  ];

  return (
    <div>
      <h3 className="mb-4 title">CMS Settings</h3>

      <div className="card">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs">
            {tabs.map((tab) => (
              <li key={tab.id} className="nav-item">
                <button
                  className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-body">
          <form onSubmit={formik.handleSubmit}>
            {activeTab === "general" && (
              <div>
                <h5 className="mb-3">General Settings</h5>
                <div className="mt-3">
                  <CustomInput
                    type="text"
                    label="Site Name"
                    name="siteName"
                    onChng={formik.handleChange("siteName")}
                    val={formik.values.siteName}
                  />
                </div>
                <div className="mt-3">
                  <label className="mb-2">Site Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    name="siteDescription"
                    onChange={formik.handleChange("siteDescription")}
                    value={formik.values.siteDescription}
                  />
                </div>
                <div className="mt-3">
                  <label className="mb-2">Logo</label>
                  {settings?.logo && (
                    <div className="mb-2">
                      <img
                        src={settings.logo}
                        alt="Logo"
                        style={{ maxHeight: "100px" }}
                      />
                    </div>
                  )}
                  <Dropzone onDrop={handleLogoUpload} accept="image/*">
                    {({ getRootProps, getInputProps }) => (
                      <div {...getRootProps()} className="border p-3 text-center">
                        <input {...getInputProps()} />
                        <p>Click or drag logo here</p>
                      </div>
                    )}
                  </Dropzone>
                </div>
                <div className="mt-3">
                  <label className="mb-2">Favicon</label>
                  {settings?.favicon && (
                    <div className="mb-2">
                      <img
                        src={settings.favicon}
                        alt="Favicon"
                        style={{ maxHeight: "50px" }}
                      />
                    </div>
                  )}
                  <Dropzone onDrop={handleFaviconUpload} accept="image/*">
                    {({ getRootProps, getInputProps }) => (
                      <div {...getRootProps()} className="border p-3 text-center">
                        <input {...getInputProps()} />
                        <p>Click or drag favicon here</p>
                      </div>
                    )}
                  </Dropzone>
                </div>
              </div>
            )}

            {activeTab === "contact" && (
              <div>
                <h5 className="mb-3">Contact Information</h5>
                <div className="mt-3">
                  <CustomInput
                    type="email"
                    label="Contact Email"
                    name="contactEmail"
                    onChng={formik.handleChange("contactEmail")}
                    val={formik.values.contactEmail}
                  />
                </div>
                <div className="mt-3">
                  <CustomInput
                    type="text"
                    label="Contact Phone"
                    name="contactPhone"
                    onChng={formik.handleChange("contactPhone")}
                    val={formik.values.contactPhone}
                  />
                </div>
                <div className="mt-3">
                  <label className="mb-2">Contact Address</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    name="contactAddress"
                    onChange={formik.handleChange("contactAddress")}
                    value={formik.values.contactAddress}
                  />
                </div>
              </div>
            )}

            {activeTab === "social" && (
              <div>
                <h5 className="mb-3">Social Media Links</h5>
                <div className="mt-3">
                  <CustomInput
                    type="url"
                    label="Facebook"
                    name="socialMedia.facebook"
                    onChng={(e) =>
                      formik.setFieldValue("socialMedia.facebook", e.target.value)
                    }
                    val={formik.values.socialMedia.facebook}
                  />
                </div>
                <div className="mt-3">
                  <CustomInput
                    type="url"
                    label="Twitter"
                    name="socialMedia.twitter"
                    onChng={(e) =>
                      formik.setFieldValue("socialMedia.twitter", e.target.value)
                    }
                    val={formik.values.socialMedia.twitter}
                  />
                </div>
                <div className="mt-3">
                  <CustomInput
                    type="url"
                    label="Instagram"
                    name="socialMedia.instagram"
                    onChng={(e) =>
                      formik.setFieldValue("socialMedia.instagram", e.target.value)
                    }
                    val={formik.values.socialMedia.instagram}
                  />
                </div>
                <div className="mt-3">
                  <CustomInput
                    type="url"
                    label="LinkedIn"
                    name="socialMedia.linkedin"
                    onChng={(e) =>
                      formik.setFieldValue("socialMedia.linkedin", e.target.value)
                    }
                    val={formik.values.socialMedia.linkedin}
                  />
                </div>
                <div className="mt-3">
                  <CustomInput
                    type="url"
                    label="YouTube"
                    name="socialMedia.youtube"
                    onChng={(e) =>
                      formik.setFieldValue("socialMedia.youtube", e.target.value)
                    }
                    val={formik.values.socialMedia.youtube}
                  />
                </div>
              </div>
            )}

            {activeTab === "payment" && (
              <div>
                <h5 className="mb-3">Payment Methods</h5>
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formik.values.paymentMethods.stripe.enabled}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "paymentMethods.stripe.enabled",
                            e.target.checked
                          )
                        }
                      />
                      <label className="form-check-label">Stripe Enabled</label>
                    </div>
                    <CustomInput
                      type="text"
                      label="Stripe Public Key"
                      value={formik.values.paymentMethods.stripe.publicKey}
                      onChng={(e) =>
                        formik.setFieldValue(
                          "paymentMethods.stripe.publicKey",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formik.values.paymentMethods.razorpay.enabled}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "paymentMethods.razorpay.enabled",
                            e.target.checked
                          )
                        }
                      />
                      <label className="form-check-label">
                        Razorpay Enabled
                      </label>
                    </div>
                    <CustomInput
                      type="text"
                      label="Razorpay Key ID"
                      value={formik.values.paymentMethods.razorpay.keyId}
                      onChng={(e) =>
                        formik.setFieldValue(
                          "paymentMethods.razorpay.keyId",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
                <div className="card">
                  <div className="card-body">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={formik.values.paymentMethods.cod.enabled}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "paymentMethods.cod.enabled",
                            e.target.checked
                          )
                        }
                      />
                      <label className="form-check-label">
                        Cash on Delivery Enabled
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "shipping" && (
              <div>
                <h5 className="mb-3">Shipping Settings</h5>
                <div className="mt-3">
                  <CustomInput
                    type="number"
                    label="Free Shipping Threshold"
                    value={formik.values.shipping.freeShippingThreshold}
                    onChng={(e) =>
                      formik.setFieldValue(
                        "shipping.freeShippingThreshold",
                        parseFloat(e.target.value)
                      )
                    }
                  />
                </div>
                <div className="mt-3">
                  <CustomInput
                    type="number"
                    label="Default Shipping Cost"
                    value={formik.values.shipping.defaultShippingCost}
                    onChng={(e) =>
                      formik.setFieldValue(
                        "shipping.defaultShippingCost",
                        parseFloat(e.target.value)
                      )
                    }
                  />
                </div>
              </div>
            )}

            {activeTab === "seo" && (
              <div>
                <h5 className="mb-3">SEO Settings</h5>
                <div className="mt-3">
                  <CustomInput
                    type="text"
                    label="Default Meta Title"
                    value={formik.values.seo.defaultMetaTitle}
                    onChng={(e) =>
                      formik.setFieldValue("seo.defaultMetaTitle", e.target.value)
                    }
                  />
                </div>
                <div className="mt-3">
                  <label className="mb-2">Default Meta Description</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formik.values.seo.defaultMetaDescription}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "seo.defaultMetaDescription",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div className="mt-3">
                  <CustomInput
                    type="text"
                    label="Default Meta Keywords"
                    value={formik.values.seo.defaultMetaKeywords}
                    onChng={(e) =>
                      formik.setFieldValue("seo.defaultMetaKeywords", e.target.value)
                    }
                  />
                </div>
                <div className="mt-3">
                  <CustomInput
                    type="text"
                    label="Google Analytics ID"
                    value={formik.values.seo.googleAnalyticsId}
                    onChng={(e) =>
                      formik.setFieldValue("seo.googleAnalyticsId", e.target.value)
                    }
                  />
                </div>
                <div className="mt-3">
                  <CustomInput
                    type="text"
                    label="Facebook Pixel ID"
                    value={formik.values.seo.facebookPixelId}
                    onChng={(e) =>
                      formik.setFieldValue("seo.facebookPixelId", e.target.value)
                    }
                  />
                </div>
              </div>
            )}

            {activeTab === "maintenance" && (
              <div>
                <h5 className="mb-3">Maintenance Mode</h5>
                <div className="mt-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formik.values.maintenance.isMaintenanceMode}
                      onChange={(e) =>
                        formik.setFieldValue(
                          "maintenance.isMaintenanceMode",
                          e.target.checked
                        )
                      }
                    />
                    <label className="form-check-label">
                      Enable Maintenance Mode
                    </label>
                  </div>
                </div>
                <div className="mt-3">
                  <label className="mb-2">Maintenance Message</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formik.values.maintenance.maintenanceMessage}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "maintenance.maintenanceMessage",
                        e.target.value
                      )
                    }
                  />
                </div>
              </div>
            )}

            {activeTab === "currency" && (
              <div>
                <h5 className="mb-3">Currency Settings</h5>
                <div className="mt-3">
                  <CustomInput
                    type="text"
                    label="Currency Symbol"
                    value={formik.values.currency.symbol}
                    onChng={(e) =>
                      formik.setFieldValue("currency.symbol", e.target.value)
                    }
                  />
                </div>
                <div className="mt-3">
                  <CustomInput
                    type="text"
                    label="Currency Code"
                    value={formik.values.currency.code}
                    onChng={(e) =>
                      formik.setFieldValue("currency.code", e.target.value)
                    }
                  />
                </div>
              </div>
            )}

            <button
              className="btn btn-success border-0 rounded-3 my-5"
              type="submit"
            >
              Update Settings
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;

